import {ApiQuery} from '../../../../gen/api';
import {useNotification} from '../../../../NotificationContext';

/** The API accepts at most this many products per run. */
export const MAX_ANALYTICS_PRODUCTS = 200;

/**
 * Queues price analytics for products: a check of their own eBay listings and a
 * fresh eBay market search. The worker process runs it; the dashboard shows the
 * result about a minute later.
 */
export function useRunPriceAnalytics() {
    const notify = useNotification();
    const mutation = ApiQuery.useAdminRunAnalyticsMutation({
        onSuccess: (data) => {
            if (data.products === 0) {
                notify.openNotificationWithIcon(
                    'warning',
                    'Nothing to analyze',
                    'Only active products with marketplace listings are analyzed.'
                );
                return;
            }
            const skipped = data.notInSale
                ? ` ${data.notInSale} skipped: not active or without marketplace listings.`
                : '';
            notify.openNotificationWithIcon(
                'success',
                `Price analytics queued for ${data.products} product${data.products === 1 ? '' : 's'}`,
                `Checks ${data.listingChecks} eBay listing${data.listingChecks === 1 ? '' : 's'} and searches the eBay market. Results appear on the dashboard once the worker has run them.${skipped}`
            );
        },
        onError: () => notify.openNotificationWithIcon('error', 'Failed to queue price analytics'),
    });

    const run = (productIds: string[]) => {
        if (productIds.length === 0) {
            return;
        }
        if (productIds.length > MAX_ANALYTICS_PRODUCTS) {
            notify.openNotificationWithIcon(
                'warning',
                `Select at most ${MAX_ANALYTICS_PRODUCTS} products`,
                `${productIds.length} products are selected.`
            );
            return;
        }
        mutation.mutate({params: {productIds}});
    };

    return {run, isRunning: mutation.isLoading};
}
